import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { error as logError } from '../../lib/logger';
import CardNB from '../ui-nb/CardNB';
import ButtonNB from '../ui-nb/ButtonNB';

interface PushSubscribeProps {
  orderId: string;
  releaseCode: string;
  vapidPublicKey: string;
}

type SubscribeState = 'unsupported' | 'idle' | 'prompting' | 'subscribed' | 'blocked' | 'error';

/**
 * Triggers the native browser push notification dialog.
 * Uses the Web Push API — no email/phone required.
 *
 * Flow:
 * 1. Register push-sw.js service worker (if not already)
 * 2. Request Notification.permission via native browser dialog
 * 3. If granted, call PushManager.subscribe() with VAPID public key
 * 4. Extract p256dh and auth keys from PushSubscription
 * 5. Store both keys in push_subscriptions table via RPC
 */
export default function PushSubscribe({ orderId, releaseCode, vapidPublicKey }: PushSubscribeProps) {
  const [state, setState] = useState<SubscribeState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Check browser support and existing permission on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setState('unsupported');
      return;
    }

    // Check if notifications are blocked at the browser level
    if (Notification.permission === 'denied') {
      setState('blocked');
      return;
    }

    // Check if already subscribed in this browser
    async function checkExisting() {
      try {
        const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
        if (!registration) return;

        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          setState('subscribed');
        }
      } catch {
        // Silently fail — user can still try to subscribe
      }
    }

    checkExisting();
  }, [orderId]);

  const handleSubscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported');
      return;
    }

    setState('prompting');
    setErrorMsg(null);

    try {
      // Step 1: Register the service worker
      const registration = await navigator.serviceWorker.register('/push-sw.js', {
        scope: '/',
      });
      await navigator.serviceWorker.ready;

      // Step 2: Request native browser permission dialog
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        setState('blocked');
        return;
      }
      if (permission !== 'granted') {
        setState('idle');
        return;
      }

      // Step 3: Subscribe to push
      if (!vapidPublicKey) {
        setErrorMsg('Push notifications not configured yet.');
        setState('error');
        return;
      }

      // Convert base64url to Uint8Array for PushManager
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });

      // Step 4: Store subscription in Supabase via SECURITY DEFINER RPC
      // (bypasses RLS — same pattern as get_public_order_by_code)
      const p256dhKey = subscription.getKey('p256dh');
      const authKey = subscription.getKey('auth');

      const { error: rpcError } = await supabase.rpc('upsert_push_subscription', {
        p_order_id: orderId,
        p_endpoint: subscription.endpoint,
        p_auth_key: p256dhKey
          ? btoa(String.fromCharCode(...new Uint8Array(p256dhKey)))
          : '',
        p_auth_secret: authKey
          ? btoa(String.fromCharCode(...new Uint8Array(authKey)))
          : '',
      });

      if (rpcError) {
        logError('Failed to store push subscription', { error: rpcError.message ?? String(rpcError) });
        setErrorMsg('Could not save your notification preference.');
        setState('error');
        return;
      }

      setState('subscribed');
    } catch (err) {
      logError('Push subscription error', { error: err instanceof Error ? err.message : String(err) });
      setErrorMsg('Something went wrong. Please try again.');
      setState('error');
    }
  }, [orderId, vapidPublicKey]);

  if (state === 'unsupported') {
    return null;
  }

  if (state === 'blocked') {
    return (
      <CardNB className="bg-amber-50 border-2 border-amber-200 mb-6">
        <div className="flex items-start gap-3">
          <span className="text-lg mt-0.5">🔕</span>
          <div>
            <p className="font-medium text-amber-800">Notifications blocked</p>
            <p className="text-sm text-amber-700 mt-1">
              This site can't send you notifications because your browser has blocked them.
              To enable: click the lock/info icon in your address bar → find "Notifications" → change to "Allow".
            </p>
          </div>
        </div>
      </CardNB>
    );
  }

  if (state === 'subscribed') {
    return (
      <CardNB className="bg-green-50 border-2 border-green-200">
        <div className="flex items-center gap-3">
          <span className="text-lg">🔔</span>
          <div>
            <p className="font-medium text-green-800">Notifications enabled</p>
            <p className="text-sm text-green-700">
              You'll receive a notification when the status of <strong>{releaseCode}</strong> changes.
            </p>
          </div>
        </div>
      </CardNB>
    );
  }

  return (
    <CardNB className="mb-6" data-testid="push-subscribe">
      <div className="text-center">
        <span className="text-3xl mb-3 block">🔔</span>
        <h3 className="font-semibold text-black mb-2">Stay updated</h3>
        <p className="text-nb-text-secondary text-sm mb-4">
          Get notified right here when your repair status changes — no email or phone needed.
        </p>

        {errorMsg && (
          <p className="text-nb-red text-sm mb-3">{errorMsg}</p>
        )}

        <ButtonNB
          variant="primary"
          onClick={handleSubscribe}
          loading={state === 'prompting'}
          className="w-full"
        >
          {state === 'prompting' ? 'Requesting permission...' : 'Notify me'}
        </ButtonNB>
      </div>
    </CardNB>
  );
}

/**
 * Convert a base64url string to a Uint8Array.
 * Required by PushManager.subscribe() for the applicationServerKey.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
